import { History } from 'history';
import { OptionsWithExtraProps, SnackbarKey, SnackbarMessage } from 'notistack';
import type { WorkySignInResponse } from '../remoteExecution/RemoteExecutionServiceImpl';
import { WORKY_WEB_APP_PATHS } from '../WorkyWebAppPaths';
import type {
    CognitoSignInEmailNotVerifiedOutcome,
    CognitoSignInFailureOutcome,
    CognitoSignInMfaRequiredOutcome,
    CognitoSignInNewPasswordRequiredOutcome,
    CognitoSignInOutcome,
    CognitoSignInTokensOutcome,
} from './WorkyCognitoAuthServiceInterfaces';
import { CognitoSignInOutcomeType } from './WorkyCognitoAuthServiceInterfaces';
import { completeCustomSignIn } from './WorkyTokenSignInService';
import { navigateAfterSignIn } from './WorkyPostLoginNavigationService';
import { buildWorkyAuthPathWithEmail } from './WorkyAuthQueryParamService';
import {
    clearPendingMfaChallengeState,
    storePendingMfaChallengeState,
} from './WorkyPendingMfaChallengeService';
import {
    clearPendingNewPasswordChallengeState,
    storePendingNewPasswordChallengeState,
} from './WorkyPendingNewPasswordChallengeService';

type EnqueueSnackbarFn = (
    message: SnackbarMessage,
    options?: OptionsWithExtraProps<'default' | 'error' | 'success' | 'warning' | 'info'>
) => SnackbarKey;

const isCognitoSignInFailureOutcome = (
    signInOutcome: CognitoSignInOutcome
): signInOutcome is CognitoSignInFailureOutcome => {
    return signInOutcome.outcomeType === CognitoSignInOutcomeType.FAILURE;
};

const isCognitoSignInMfaRequiredOutcome = (
    signInOutcome: CognitoSignInOutcome
): signInOutcome is CognitoSignInMfaRequiredOutcome => {
    return signInOutcome.outcomeType === CognitoSignInOutcomeType.MFA_REQUIRED;
};

const isCognitoSignInNewPasswordRequiredOutcome = (
    signInOutcome: CognitoSignInOutcome
): signInOutcome is CognitoSignInNewPasswordRequiredOutcome => {
    return signInOutcome.outcomeType === CognitoSignInOutcomeType.NEW_PASSWORD_REQUIRED;
};

const isCognitoSignInEmailNotVerifiedOutcome = (
    signInOutcome: CognitoSignInOutcome
): signInOutcome is CognitoSignInEmailNotVerifiedOutcome => {
    return signInOutcome.outcomeType === CognitoSignInOutcomeType.EMAIL_NOT_VERIFIED;
};

const isCognitoSignInTokensOutcome = (
    signInOutcome: CognitoSignInOutcome
): signInOutcome is CognitoSignInTokensOutcome => {
    return signInOutcome.outcomeType === CognitoSignInOutcomeType.TOKENS;
};

const handleCognitoPasswordSignInOutcome = async ({
    signInOutcome,
    history,
    enqueueSnackbar,
    primaryWorkyCorpId,
    successMessage,
}: {
    signInOutcome: CognitoSignInOutcome;
    history: History;
    enqueueSnackbar: EnqueueSnackbarFn;
    primaryWorkyCorpId: string | null;
    successMessage: string;
}): Promise<boolean> => {
    if (isCognitoSignInFailureOutcome(signInOutcome)) {
        enqueueSnackbar(signInOutcome.errorMessage, { variant: 'error' });
        return false;
    }

    if (isCognitoSignInMfaRequiredOutcome(signInOutcome)) {
        clearPendingNewPasswordChallengeState();
        storePendingMfaChallengeState({
            emailAddress: signInOutcome.emailAddress,
            mfaChallengeStep: signInOutcome.mfaChallengeStep,
            primaryWorkyCorpId,
        });
        history.push(WORKY_WEB_APP_PATHS.authMfa);
        return false;
    }

    if (isCognitoSignInNewPasswordRequiredOutcome(signInOutcome)) {
        clearPendingMfaChallengeState();
        storePendingNewPasswordChallengeState({
            emailAddress: signInOutcome.emailAddress,
            primaryWorkyCorpId,
        });
        history.push(WORKY_WEB_APP_PATHS.authNewPassword);
        return false;
    }

    if (isCognitoSignInEmailNotVerifiedOutcome(signInOutcome)) {
        enqueueSnackbar(
            'Your email is not verified yet. Enter the confirmation code we sent you.',
            { variant: 'warning' }
        );
        history.push(
            buildWorkyAuthPathWithEmail(WORKY_WEB_APP_PATHS.verifyEmail, signInOutcome.emailAddress)
        );
        return false;
    }

    if (!isCognitoSignInTokensOutcome(signInOutcome)) {
        enqueueSnackbar('Unable to complete sign-in. Please try again.', { variant: 'error' });
        return false;
    }

    const signInResponse: WorkySignInResponse | null = await completeCustomSignIn({
        cognitoAccessToken: signInOutcome.tokens.accessToken,
        cognitoIdToken: signInOutcome.tokens.idToken,
        cognitoRefreshToken: signInOutcome.tokens.refreshToken,
        expiresInSeconds: signInOutcome.tokens.expiresInSeconds,
        primaryWorkyCorpId,
    });

    if (signInResponse === null) {
        enqueueSnackbar('Unable to complete sign-in. Please try again.', { variant: 'error' });
        return false;
    }

    clearPendingMfaChallengeState();
    clearPendingNewPasswordChallengeState();
    enqueueSnackbar(successMessage, { variant: 'success' });
    navigateAfterSignIn({
        history,
        userType: signInResponse.basicJobberDetails.userType,
        jobberStatus: signInResponse.basicJobberDetails.jobberStatus,
    });
    return true;
};

export {
    handleCognitoPasswordSignInOutcome,
    isCognitoSignInFailureOutcome,
    isCognitoSignInMfaRequiredOutcome,
    isCognitoSignInNewPasswordRequiredOutcome,
    isCognitoSignInTokensOutcome,
};
